/**
 * Presentation of an estimate: cost summary, breakdown, material list, assumptions
 * and (optionally) a side-by-side comparison. Used for fresh results and saved projects.
 */

import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Divider,
  Grid,
  IconButton,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Tabs,
  Tooltip,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import RemoveCircleOutlineRoundedIcon from '@mui/icons-material/RemoveCircleOutlineRounded';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { SectionCard } from '../ui';
import { COST_RATES, HOUSE_TYPES, ASSUMPTIONS, DISCLAIMER } from '../../utils/costRates';
import { formatCurrency, formatNumber } from '../../utils/format';

/** One-line description of an estimate's inputs */
export const describeInputs = (inputs = {}, t, language) => {
  const my = language === 'my';
  const house = HOUSE_TYPES[inputs.houseType];
  const finish = COST_RATES[inputs.finishingLevel];
  const floors = Number(inputs.floors) || 1;
  return [
    house && (my ? house.labelMy : house.label),
    inputs.builtUpArea && `${formatNumber(inputs.builtUpArea)} ${t.sqft}`,
    `${floors} ${floors === 1 ? t.storey : t.storeys}`,
    finish && (my ? finish.labelMy : finish.label),
  ]
    .filter(Boolean)
    .join(' · ');
};

// ---------------------------------------------------------------------------

const CostSummaryCard = ({ estimate, t }) => {
  const { totalCost, perSqft } = estimate.summary;
  return (
    <Card
      sx={(theme) => ({
        mb: 2,
        borderColor: alpha(theme.palette.primary.main, 0.35),
        background: `linear-gradient(180deg, ${alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.14 : 0.06)} 0%, ${theme.palette.background.paper} 100%)`,
      })}
    >
      <CardContent sx={{ p: { xs: 2.5, sm: 3 } }}>
        <Typography variant="overline" color="text.secondary">
          {t.estimatedCost}
        </Typography>
        <Typography
          className="tabular"
          sx={{ fontSize: { xs: '2.25rem', sm: '2.75rem' }, fontWeight: 800, letterSpacing: '-0.03em', lineHeight: 1.1 }}
        >
          {formatCurrency(totalCost.avg)}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          {t.likely}
        </Typography>

        <Divider sx={{ my: 2 }} />

        <Grid container spacing={2}>
          <Grid item xs={12} sm={7}>
            <Typography variant="caption" color="text.secondary">
              {t.range}
            </Typography>
            <Typography className="tabular" variant="subtitle1">
              {formatCurrency(totalCost.min)} – {formatCurrency(totalCost.max)}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={5}>
            <Typography variant="caption" color="text.secondary">
              {t.costPerSqft}
            </Typography>
            <Typography className="tabular" variant="subtitle1">
              RM {formatNumber(perSqft.min)} – {formatNumber(perSqft.max)}
            </Typography>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
};

/** Horizontal bars: one row per budget category (single hue, direct labels) */
const CostBreakdown = ({ categories = [], t, language }) => {
  const my = language === 'my';
  const max = Math.max(...categories.map((c) => c.percentage || 0), 1);
  return (
    <SectionCard title={t.breakdown} subtitle={t.breakdownHint}>
      <Stack spacing={1.75}>
        {categories.map((c) => {
          const label = my ? c.labelMy : c.label;
          return (
            <Tooltip key={c.id} title={`${label}: ${formatCurrency(c.amount)} (${c.percentage}%)`} placement="top-start">
              <Box>
                <Stack direction="row" justifyContent="space-between" alignItems="baseline" spacing={2}>
                  <Typography variant="body2" fontWeight={500}>
                    {label}
                  </Typography>
                  <Typography variant="body2" className="tabular" fontWeight={700} sx={{ flexShrink: 0 }}>
                    {formatCurrency(c.amount)}
                    <Box component="span" sx={{ color: 'text.secondary', fontWeight: 500, ml: 1 }}>
                      {c.percentage}%
                    </Box>
                  </Typography>
                </Stack>
                <Box sx={{ mt: 0.75, height: 8, borderRadius: '4px', bgcolor: 'action.hover' }}>
                  <Box
                    sx={{
                      width: `${(c.percentage / max) * 100}%`,
                      height: '100%',
                      borderRadius: '4px',
                      bgcolor: 'primary.main',
                    }}
                  />
                </Box>
              </Box>
            </Tooltip>
          );
        })}
      </Stack>
    </SectionCard>
  );
};

const QuickAssumptions = ({ inputs, t }) => {
  const items = [
    `${inputs.bedrooms} ${t.bedrooms.toLowerCase()}`,
    `${inputs.bathrooms} ${t.bathrooms.toLowerCase()}`,
    t[inputs.wallType],
    t[inputs.foundationType],
    t[inputs.roofType],
    `${t.ceilingType}: ${t[inputs.ceilingType]}`,
  ].filter(Boolean);
  return (
    <Box sx={{ mt: 0.5 }}>
      <Typography variant="caption" color="text.secondary" component="div" sx={{ mb: 1 }}>
        {t.quickAssumed}
      </Typography>
      <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', rowGap: 1 }}>
        {items.map((label) => (
          <Chip key={label} size="small" variant="outlined" label={label} />
        ))}
      </Stack>
    </Box>
  );
};

const MaterialsList = ({ groups = [], legacy, t, language }) => {
  const my = language === 'my';
  return (
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {legacy ? t.materialsLegacy : t.materialsHint}
      </Typography>
      {groups.map((group) => (
        <SectionCard key={group.id} title={my ? group.labelMy : group.label} contentSx={{ px: 0, pb: 0.5 }}>
          <Table size="small">
            <TableBody>
              {group.items.map((it) => (
                <TableRow key={it.id} sx={{ '&:last-child td': { borderBottom: 0 } }}>
                  <TableCell sx={{ pl: { xs: 2, sm: 2.5 }, py: 1.25 }}>
                    <Typography variant="body2" fontWeight={600}>
                      {my ? it.labelMy : it.label}
                    </Typography>
                    {(it.note || it.noteMy) && (
                      <Typography variant="caption" color="text.secondary">
                        {my ? it.noteMy : it.note}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell align="right" sx={{ pr: { xs: 2, sm: 2.5 }, py: 1.25, whiteSpace: 'nowrap' }}>
                    <Typography variant="body2" fontWeight={700} className="tabular">
                      {formatNumber(it.avg)}{' '}
                      <Box component="span" sx={{ fontWeight: 500, color: 'text.secondary' }}>
                        {my ? it.unitMy : it.unit}
                      </Box>
                    </Typography>
                    {it.min !== it.max && (
                      <Typography variant="caption" color="text.secondary" className="tabular">
                        {formatNumber(it.min)} – {formatNumber(it.max)}
                      </Typography>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </SectionCard>
      ))}
    </Box>
  );
};

export const AssumptionsPanel = ({ t, language }) => {
  const my = language === 'my';
  const disclaimer = DISCLAIMER[my ? 'my' : 'en'];
  return (
    <Box>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6}>
          <SectionCard title={t.included} sx={{ height: '100%', mb: 0 }}>
            <Stack spacing={1}>
              {ASSUMPTIONS.included.map((a) => (
                <Stack key={a.en} direction="row" spacing={1} alignItems="flex-start">
                  <CheckRoundedIcon color="success" sx={{ fontSize: 18, mt: '2px' }} />
                  <Typography variant="body2">{my ? a.my : a.en}</Typography>
                </Stack>
              ))}
            </Stack>
          </SectionCard>
        </Grid>
        <Grid item xs={12} sm={6}>
          <SectionCard title={t.notIncluded} sx={{ height: '100%', mb: 0 }}>
            <Stack spacing={1}>
              {ASSUMPTIONS.excluded.map((a) => (
                <Stack key={a.en} direction="row" spacing={1} alignItems="flex-start">
                  <RemoveCircleOutlineRoundedIcon color="action" sx={{ fontSize: 18, mt: '2px' }} />
                  <Typography variant="body2">{my ? a.my : a.en}</Typography>
                </Stack>
              ))}
            </Stack>
          </SectionCard>
        </Grid>
      </Grid>
      <Alert severity="warning" icon={<InfoOutlinedIcon />} sx={{ mt: 2, alignItems: 'flex-start' }}>
        <Typography variant="subtitle2" gutterBottom>
          {disclaimer.title}
        </Typography>
        {disclaimer.content.map((line) => (
          <Typography key={line} variant="body2" sx={{ mb: 0.5 }}>
            {line}
          </Typography>
        ))}
      </Alert>
    </Box>
  );
};

const NotIncludedStrip = ({ t, onMore }) => {
  return (
    <Box sx={{ mb: 2, px: 0.5 }}>
      <Typography variant="caption" color="text.secondary" component="span" sx={{ fontWeight: 600, mr: 1 }}>
        {t.notIncluded}:
      </Typography>
      <Typography variant="caption" color="text.secondary" component="span">
        {t.notIncludedShort}{' '}
      </Typography>
      <Box
        component="button"
        type="button"
        onClick={onMore}
        sx={{
          border: 0,
          p: 0,
          bgcolor: 'transparent',
          color: 'primary.main',
          font: 'inherit',
          typography: 'caption',
          fontWeight: 600,
          cursor: 'pointer',
        }}
      >
        {t.tabAssumptions} →
      </Box>
    </Box>
  );
};

// ---------------------------------------------------------------------------

const findItem = (estimate, id) => {
  for (const g of estimate.materialGroups || []) {
    const found = g.items.find((i) => i.id === id);
    if (found) return found;
  }
  return null;
};

const CompareCard = ({ estimate, compare, t, language, onClear }) => {
  const my = language === 'my';
  const diff = compare.summary.totalCost.avg - estimate.summary.totalCost.avg;
  const rows = [
    {
      label: t.total,
      a: formatCurrency(estimate.summary.totalCost.avg),
      b: formatCurrency(compare.summary.totalCost.avg),
      d: `${diff >= 0 ? '+' : '−'}${formatCurrency(Math.abs(diff))}`,
      strong: true,
    },
    {
      label: t.range,
      a: `${formatCurrency(estimate.summary.totalCost.min, { compact: true })} – ${formatCurrency(estimate.summary.totalCost.max, { compact: true })}`,
      b: `${formatCurrency(compare.summary.totalCost.min, { compact: true })} – ${formatCurrency(compare.summary.totalCost.max, { compact: true })}`,
    },
    {
      label: t.costPerSqft,
      a: `RM ${estimate.summary.perSqft.avg}`,
      b: `RM ${compare.summary.perSqft.avg}`,
    },
    ...['cement', 'reinforcement', 'bricks', 'floorTiles', 'paint']
      .map((id) => {
        const a = findItem(estimate, id);
        const b = findItem(compare, id);
        if (!a || !b) return null;
        return {
          label: my ? a.labelMy : a.label,
          a: `${formatNumber(a.avg)} ${my ? a.unitMy : a.unit}`,
          b: `${formatNumber(b.avg)} ${my ? b.unitMy : b.unit}`,
        };
      })
      .filter(Boolean),
  ];

  // Label on its own line on phones; label + two values in one row on wider screens
  const grid = { display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', sm: '1.1fr 1fr 1fr' }, columnGap: 2, alignItems: 'baseline' };
  const labelSx = { gridColumn: { xs: '1 / -1', sm: 'auto' }, color: 'text.secondary' };

  return (
    <SectionCard
      title={t.compareTitle}
      action={
        <Tooltip title={t.removeCompare}>
          <IconButton size="small" onClick={onClear} aria-label={t.removeCompare}>
            <CloseRoundedIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      }
    >
      <Box sx={{ ...grid, pb: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
        <Box sx={{ display: { xs: 'none', sm: 'block' } }} />
        {[
          [t.current, estimate],
          [t.scenario, compare],
        ].map(([label, e]) => (
          <Box key={label} sx={{ minWidth: 0 }}>
            <Typography variant="subtitle2">{label}</Typography>
            <Typography variant="caption" color="text.secondary">
              {describeInputs(e.inputs, t, language)}
            </Typography>
          </Box>
        ))}
      </Box>
      {rows.map((r) => (
        <Box key={r.label} sx={{ ...grid, py: 1.25, borderBottom: '1px solid', borderColor: 'divider', '&:last-of-type': { borderBottom: 0 } }}>
          <Typography variant="body2" sx={labelSx}>
            {r.label}
          </Typography>
          <Typography variant="body2" className="tabular" fontWeight={r.strong ? 700 : 500}>
            {r.a}
          </Typography>
          <Box>
            <Typography variant="body2" className="tabular" fontWeight={r.strong ? 700 : 500}>
              {r.b}
            </Typography>
            {r.d && (
              <Typography variant="caption" className="tabular" color={diff > 0 ? 'error.main' : 'success.main'} fontWeight={700}>
                {t.difference}: {r.d}
              </Typography>
            )}
          </Box>
        </Box>
      ))}
    </SectionCard>
  );
};

// ---------------------------------------------------------------------------

const EstimateResultView = ({ estimate, compare, onClearCompare, onRefine, t, language }) => {
  const [tab, setTab] = useState(0);

  return (
    <Box>
      <CostSummaryCard estimate={estimate} t={t} />
      <NotIncludedStrip t={t} onMore={() => setTab(2)} />

      {compare && <CompareCard estimate={estimate} compare={compare} t={t} language={language} onClear={onClearCompare} />}

      <Tabs value={tab} onChange={(e, v) => setTab(v)} sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }} variant="fullWidth">
        <Tab label={t.tabSummary} />
        <Tab label={t.tabMaterials} />
        <Tab label={t.tabAssumptions} />
      </Tabs>

      {tab === 0 && (
        <Box>
          <CostBreakdown categories={estimate.summary.categories} t={t} language={language} />
          {estimate.mode === 'quick' && !estimate.legacy && (
            <SectionCard>
              <QuickAssumptions inputs={estimate.inputs} t={t} />
              {onRefine && (
                <Button variant="outlined" size="small" onClick={onRefine} sx={{ mt: 2 }}>
                  {t.refine}
                </Button>
              )}
            </SectionCard>
          )}
        </Box>
      )}
      {tab === 1 && <MaterialsList groups={estimate.materialGroups} legacy={estimate.legacy} t={t} language={language} />}
      {tab === 2 && <AssumptionsPanel t={t} language={language} />}
    </Box>
  );
};

export default EstimateResultView;
